import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useResolvedElement } from "./useResolvedElement.js";
function extractSize(entry, boxProp, sizeType) {
  if (!entry[boxProp]) {
    if (boxProp === "contentBoxSize") {
      return entry.contentRect[sizeType === "inlineSize" ? "width" : "height"];
    }
    return void 0;
  }
  return entry[boxProp][0] ? entry[boxProp][0][sizeType] : (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    entry[boxProp][sizeType]
  );
}
function useResizeObserver(opts = {}) {
  const { onResize } = opts;
  const onResizeRef = useRef(void 0);
  onResizeRef.current = onResize;
  const round = opts.round || Math.round;
  const resizeObserverRef = useRef();
  const [size, setSize] = useState({
    width: void 0,
    height: void 0
  });
  const didUnmount = useRef(false);
  useEffect(() => {
    didUnmount.current = false;
    return () => {
      didUnmount.current = true;
    };
  }, []);
  const previous = useRef({
    width: void 0,
    height: void 0
  });
  const refCallback = useResolvedElement(
    useCallback(
      (element) => {
        if (!resizeObserverRef.current || resizeObserverRef.current.box !== opts.box || resizeObserverRef.current.round !== round) {
          resizeObserverRef.current = {
            box: opts.box,
            round,
            instance: new ResizeObserver((entries) => {
              const entry = entries[0];
              const boxProp = (
                // eslint-disable-next-line no-nested-ternary
                opts.box === "border-box" ? "borderBoxSize" : opts.box === "device-pixel-content-box" ? "devicePixelContentBoxSize" : "contentBoxSize"
              );
              const reportedWidth = extractSize(entry, boxProp, "inlineSize");
              const reportedHeight = extractSize(entry, boxProp, "blockSize");
              const newWidth = reportedWidth ? round(reportedWidth) : void 0;
              const newHeight = reportedHeight ? round(reportedHeight) : void 0;
              if (previous.current.width !== newWidth || previous.current.height !== newHeight) {
                const newSize = { width: newWidth, height: newHeight };
                previous.current.width = newWidth;
                previous.current.height = newHeight;
                if (onResizeRef.current) {
                  onResizeRef.current(newSize);
                } else if (!didUnmount.current) {
                  setSize(newSize);
                }
              }
            })
          };
        }
        resizeObserverRef.current.instance.observe(element, { box: opts.box });
        return () => {
          if (resizeObserverRef.current) {
            resizeObserverRef.current.instance.unobserve(element);
          }
        };
      },
      [opts.box, round]
    ),
    opts.ref
  );
  return useMemo(
    () => ({
      ref: refCallback,
      width: size.width,
      height: size.height
    }),
    [refCallback, size.width, size.height]
  );
}
export {
  useResizeObserver
};
